import React, { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { withTypes, Field, FormRenderProps } from 'react-final-form';
import type { WCmsPhotoPageView, WCmsPhotoView, WCmsVideoView } from '@zola/svc-web-api-ts-client';

import Modal, { ModalProps } from '@zola/zola-ui/src/components/Modal/Modal';
import FormInput from '@zola/zola-ui/src/components/Form/inputV3/FinalFormInput';
import FormTextarea from '@zola/zola-ui/src/components/Form/TextareaFieldV2/TextareaFieldV2Adapter';

import {
  REQUIRED,
  MAX_LENGTH_200,
  YOUTUBE_OR_VIMEO,
} from '@zola/zola-ui/src/components/Form/util/validations';
import { composeValidators } from '@zola/zola-ui/src/components/Form/util/composeValidators';

import type { GenericEntityType } from 'actions/types/website/EntityActionTypes';
import type { AppDispatch } from 'reducers/index';

import {
  addVideo,
  updateVideo,
  CreateVideoRequestType,
} from 'actions/website/entities/VideosActions';

import { getCookie } from '@zola-helpers/client/dist/es/util/storage';
import { WEBSITE_V2 } from 'util/experimentFlags';
import {
  useEditWebsiteActions,
  EntitiesPropertyNames,
} from 'components/manage/EditWebsite/context';
import { getGalleryPage } from 'components/manage/EditWebsite/api/photos';

import {
  Container,
  Title,
  Subtitle,
  StyledButton,
  InputContainer,
} from './WebsiteVideoUploadModal.styles';

type WebsiteVideoUploadModalProps = Pick<ModalProps, 'triggerRef' | 'onClose'> & {
  entity?: GenericEntityType;
  page?: 'home' | 'gallery';
};

const WebsiteVideoUploadModal = ({
  entity = {},
  page = 'gallery',
  triggerRef,
  onClose,
}: WebsiteVideoUploadModalProps): JSX.Element => {
  const dispatch = useDispatch<AppDispatch>();
  const [isWebsiteV2, setIsWebsiteV2] = useState(false);
  const dialogHeadingId = 'website-video-header';
  const { id, url, caption } = entity;

  const {
    actions: { receiveEntities },
  } = useEditWebsiteActions();

  useEffect(() => {
    if (getCookie(WEBSITE_V2)) {
      setIsWebsiteV2(true);
    }
  }, []);

  const onFormSave = (values: CreateVideoRequestType) =>
    dispatch(id ? updateVideo({ video_id: id, ...values }) : addVideo(values, page))
      .then(() => {
        if (isWebsiteV2) {
          getGalleryPage()
            .then((pageData: WCmsPhotoPageView) => {
              receiveEntities({
                [EntitiesPropertyNames.photos]: pageData.photos as WCmsPhotoView[],
                [EntitiesPropertyNames.videos]: pageData.videos as WCmsVideoView[],
              });
            })
            .catch(() => {
              // do nothing
            });
        }
      })
      .then(onClose);
  const { Form: FinalForm } = withTypes<CreateVideoRequestType>();

  const initialValues = {
    url,
    caption,
  };

  return (
    <Modal
      onClose={onClose}
      rootId="zola-wedding-container"
      triggerRef={triggerRef}
      dialogHeadingId={dialogHeadingId}
    >
      <Container>
        <Title id={dialogHeadingId}>Add your video</Title>
        <Subtitle>For the best quality, we recommend Vimeo.</Subtitle>
        <FinalForm onSubmit={onFormSave} initialValues={initialValues}>
          {({ handleSubmit, invalid, pristine }: FormRenderProps): JSX.Element => (
            <form onSubmit={handleSubmit}>
              <InputContainer>
                <FormInput
                  name="url"
                  label="Vimeo or YouTube URL"
                  placeholder="Paste it here"
                  isRequired
                  validate={[REQUIRED, YOUTUBE_OR_VIMEO]}
                />
              </InputContainer>
              <Field
                name="caption"
                id="videoCaption"
                label="Caption"
                validate={composeValidators(MAX_LENGTH_200)}
                maxChars={200}
                placeholder="This will appear on your website below the video (optional)."
                component={FormTextarea}
              />
              <StyledButton type="submit" disabled={invalid || pristine}>
                All Set
              </StyledButton>
            </form>
          )}
        </FinalForm>
      </Container>
    </Modal>
  );
};

export default WebsiteVideoUploadModal;
